<script setup lang="ts">
/**
 * Calendario de salidas de un dive center (§20b).
 *
 * Arranca en vista **semana**: es la que responde la pregunta de todos los días
 * ("cómo viene la semana"). El día se lee mejor como lista —"quién sale hoy"—
 * así que la vista Day es `listDay` y no una grilla horaria.
 */
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import type { CalendarOptions, EventClickArg, DatesSetArg } from '@fullcalendar/core'

import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import { useTheme } from 'vuetify'
import { createEvent, createEventDaySheetLink, deleteEvent, getEvent, listEvents, updateEvent, type CreateEventPayload, type UpdateEventPayload } from '@/api/events'
import { createBooking } from '@/api/bookings'
import { ApiError, apiOrigin } from '@/api/client'
import { getDiveCenter } from '@/api/dive-centers'
import { listOfferings } from '@/api/offerings'
import { listTeam, type TeamMember } from '@/api/team'
import AlertBox from '@/components/AlertBox.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import ClosuresDrawer from '@/components/ClosuresDrawer.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import EventCreateDrawer from '@/components/EventCreateDrawer.vue'
import EventDetailDrawer from '@/components/EventDetailDrawer.vue'
import ActivityListView from '@/components/ActivityListView.vue'
import BulkManageDrawer from '@/components/BulkManageDrawer.vue'
import { apiMessage, notifyError, notifyInfo, notifySuccess } from '@/composables/useSnackbar'
import { usePermissions } from '@/composables/usePermissions'
import { usePreferences } from '@/composables/usePreferences'
import { useQuotaGate } from '@/composables/useQuotaGate'
import {
  browserTimezone,
  calendarEventColor,
  eventIcon,
  isOverCapacity,
  occupancyLabel,
  occupancyPercent,
  toFullCalendarEvent,
} from '@/composables/useCalendarEvents'
import { labelForOffering } from '@/data/offerings'
import { serviceColor } from '@/data/serviceColors'
import type { CalendarEvent, DiveCenter, DiveCenterClient, DiveCenterClosure, Offering } from '@/types'

definePage({ meta: { requiresAuth: true } })

const route = useRoute('dive-centers-id-calendar')

// El id se lee una sola vez en el setup: el `RouterView` lleva `:key` con él,
// así que cambiar de centro remonta el componente entero (§18.5).
const diveCenterId = String(route.params.id)

// El cupo del plan: apaga el alta de actividades y explica el rechazo (§33).
const { blocked: quotaBlocked, guard: quotaGuard } = useQuotaGate()

/*
 * Tema y preferencias regionales de la cuenta.
 *
 * El calendario venía con el reloj de 24 h y el lunes como primer día
 * cableados, y con el color del bloque calculado para fondo claro. Los tres son
 * decisiones que el usuario ya tomó en Preferences o en el selector de tema, así
 * que salen de ahí en vez de estar fijos acá.
 */
const theme = useTheme()
const isDark = computed(() => theme.current.value.dark)

const { prefs, formatDate, formatTime } = usePreferences()

/*
 * Los días de cierre son configuración del centro, aunque se editen desde acá.
 * El resto del calendario —programar salidas, anotar gente, el roster— sigue
 * abierto a staff: es exactamente su trabajo.
 */
const { canConfigureCenter } = usePermissions()

const hour12 = computed(() => prefs.value.timeFormat === '12h')
const firstDay = computed(() => (prefs.value.weekStartsOn === 'sunday' ? 0 : 1))

/** Un evento de Blue Trails listo para FullCalendar, pintado para el tema actual. */
const toFcEvent = (e: CalendarEvent) =>
  toFullCalendarEvent(e, isDark.value ? 'dark' : 'light')

/*
 * Enlace directo a una actividad, desde el dashboard: `?date=&event=`.
 *
 * La fecha abre el calendario en la semana correcta —una salida de dentro de
 * tres semanas no está en el rango que se carga por defecto— y el id abre su
 * panel apenas ese rango responde. Se leen una sola vez en el setup, como el
 * `:id`: son el punto de entrada, no un estado que después cambie.
 */
const initialDateParam = String(route.query.date ?? '')
const pendingEventId = ref(String(route.query.event ?? ''))

const loading = ref(true)
const error = ref('')
const warning = ref<'no-timezone' | ''>('')

const diveCenter = ref<DiveCenter | null>(null)
const centerTimezone = ref('')

/*
 * Franja horaria que dibuja la grilla. Constantes y no literales sueltos porque
 * el sombreado de "lo que ya pasó" tiene que calcularse contra exactamente los
 * mismos límites: si se cambia uno y no el otro, la sombra queda corrida.
 */
const SLOT_MIN_HOUR = 5
const SLOT_MAX_HOUR = 23

/**
 * Qué parte del día visible ya pasó (0 a 1), **en hora del centro**.
 *
 * Alimenta la sombra de la columna de hoy. Se calcula con la zona del dive
 * center y no con la del navegador por la misma razón que todo lo demás en esta
 * pantalla: un manager en Madrid administrando un centro en Bonaire vería la
 * sombra cinco husos corrida, justo sobre las salidas que todavía no ocurrieron.
 */
const dayElapsed = ref(0)

/**
 * "Ahora" en el centro, expresado como hora **local del navegador**.
 *
 * Es la pieza que hace posible la línea roja con zonas cruzadas. Los eventos se
 * dibujan a partir de cadenas ingenuas (`2026-08-11T07:00:00`), o sea que toda
 * la grilla es "hora del centro interpretada como hora local"; devolviendo el
 * reloj del centro con ese mismo disfraz, la línea cae exactamente donde
 * corresponde. Sin esto, FullCalendar usa el reloj del navegador y la línea
 * apunta a una hora que en este calendario no existe.
 *
 * Zona vacía o inválida → el reloj del navegador tal cual: es lo único que se
 * puede decir, y sigue siendo mejor que no dibujar nada.
 */
function centerNow(): Date {
  const now = new Date()
  const tz = centerTimezone.value

  if (!tz)
    return now

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(now)

    const at = (type: string) => Number(parts.find(p => p.type === type)?.value ?? NaN)

    const y = at('year')
    const mo = at('month')
    const d = at('day')
    // `hour12: false` puede devolver 24 a la medianoche en algunos motores.
    const h = at('hour') % 24
    const mi = at('minute')
    const sec = at('second')

    if ([y, mo, d, h, mi, sec].some(v => !Number.isFinite(v)))
      return now

    return new Date(y, mo - 1, d, h, mi, sec)
  }
  catch {
    return now
  }
}

function refreshDayElapsed() {
  const now = centerNow()
  const minutes = now.getHours() * 60 + now.getMinutes()

  const from = SLOT_MIN_HOUR * 60
  const span = (SLOT_MAX_HOUR - SLOT_MIN_HOUR) * 60

  dayElapsed.value = Math.min(1, Math.max(0, (minutes - from) / span))
}

/*
 * Hasta dónde llega el calendario generado.
 *
 * Se guarda para poder explicar un mes vacío: más allá del horizonte no hay
 * salidas materializadas, y sin decirlo eso se lee como que el horario dejó de
 * producir. El tope existe porque leer el calendario **genera**: sin él,
 * apretar "mes siguiente" un rato creaba salidas hasta 2031.
 */
const horizonThrough = ref('')

/*
 * Cierres del centro (§22). Llegan con la respuesta del calendario, así que no
 * cuestan una consulta aparte, y sirven para dos cosas: sombrear el día en la
 * grilla y explicar por qué está cerrado.
 */
const closures = ref<DiveCenterClosure[]>([])
const showClosures = ref(false)

/** Cierre que se está editando; `null` = alta. El panel gestiona uno por vez. */
const editingClosure = ref<DiveCenterClosure | null>(null)

function openClosure(closure: DiveCenterClosure | null) {
  editingClosure.value = closure
  showClosures.value = true
}

/** Fecha ISO → motivo del cierre, para el sombreado y el tooltip. */
/** " — Navidad" o cadena vacía si el cierre no tiene motivo. */
const closedSuffix = (date: string) => {
  const reason = closedDays.value.get(date)

  return reason ? ` — ${reason}` : ''
}

const closedDays = computed(() => {
  const map = new Map<string, string>()

  for (const c of closures.value) {
    if (!c.from || !c.to || c.to < c.from)
      continue

    // Aritmética en UTC: un día local dura 23 o 25 horas dos veces al año y
    // sumar 24 h saltearía o repetiría un día. Acá las fechas son etiquetas.
    let cursor = Date.parse(`${c.from}T00:00:00Z`)

    const end = Date.parse(`${c.to}T00:00:00Z`)

    let guard = 0
    while (cursor <= end && guard < 400) {
      // El motivo va crudo, sin relleno: un cierre puede no tenerlo, y meter
      // "Closed" acá producía mensajes como "is closed — Closed.". Quien lo usa
      // decide qué mostrar; para saber SI está cerrado se usa `.has()`.
      map.set(new Date(cursor).toISOString().slice(0, 10), c.reason ?? '')
      cursor += 86_400_000
      guard += 1
    }
  }

  return map
})
const events = shallowRef<CalendarEvent[]>([])
const includeCancelled = ref(true)

const selected = ref<CalendarEvent | null>(null)
const showDetail = ref(false)

const offerings = ref<Offering[]>([])
/*
 * Cualquier miembro del equipo se puede asignar a una salida, no solo los de
 * rol `staff`: el dueño del centro puede ser el capitán y un admin puede dar
 * clases. Los permisos del portal y la función a bordo son dos ejes distintos
 * (`staffProfile.jobTitle`), y filtrar por rol dejaba fuera justo a la gente
 * que más sale al agua.
 */
const staff = ref<TeamMember[]>([])

/*
 * Calendario o lista.
 *
 * La grilla contesta "cómo viene la semana"; la lista contesta "qué hay entre
 * el lunes y el viernes" y deja **operar sobre varias a la vez**, que es lo que
 * pide una estadía de varios días. Son dos lecturas del mismo rango, así que
 * comparten datos, filtros y métricas: solo cambia el cuerpo.
 */
const viewMode = ref<'calendar' | 'list'>('calendar')

/*
 * Selección múltiple, por id.
 *
 * Por id y no por objeto: la lista se refiltra y se recarga mientras se elige,
 * y guardar los objetos dejaría copias viejas de actividades cuya ocupación ya
 * cambió. Se resuelven contra `events` al usarlas.
 */
const selectedIds = ref<string[]>([])
const showBulk = ref(false)

/*
 * Modo selección de la grilla.
 *
 * Es un modo y no un gesto ambiguo: fuera de él, el click abre la actividad
 * como siempre. Se evaluó "un click selecciona, doble click abre" y se
 * descartó — FullCalendar no tiene `eventDblClick`, así que habría que
 * emularlo con un temporizador y **retrasar todo click simple** unos 250 ms
 * para saber si viene un segundo. Abrir una actividad es el gesto más
 * frecuente del calendario; volverlo pegajoso para habilitar algo ocasional
 * es un mal negocio.
 *
 * No hace falta parchear nada de la librería: `eventClick` y
 * `eventClassNames` son API pública.
 */
const selectMode = ref(false)

const showEditor = ref(false)
const editorDate = ref('')
const editorTime = ref('')
const editorError = ref('')
const submitting = ref(false)
const detailError = ref('')

const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)
const calendarRef = ref<{ getApi: () => { gotoDate: (d: Date | string) => void } } | null>(null)

/*
 * Panel de navegación y filtros, plegado por defecto.
 *
 * Va plegado porque la pregunta de todos los días es "cómo viene la semana", y
 * para eso el calendario tiene que ocupar todo el ancho. Filtrar o saltar a una
 * fecha lejana son gestos ocasionales, y pagar ancho todo el tiempo por ellos
 * era un mal negocio.
 */
const showPanel = ref(true)

/*
 * Pantalla completa: el calendario tapa el resto del portal.
 *
 * Es la pantalla donde el alto vale más que en ninguna otra —una semana con
 * cuatro salidas diarias no entra sin scrollear— y el sidebar, la cabecera y
 * las métricas se llevan casi un tercio. Es un modo de trabajo, no una vista
 * aparte: se entra, se opera y se sale.
 *
 * Se resuelve con CSS y no con la Fullscreen API del navegador porque los
 * paneles y diálogos de Vuetify se montan colgando del `body`, fuera del
 * elemento que se pondría en pantalla completa: en modo nativo abrir una salida
 * no mostraría nada.
 */
const fullscreen = ref(false)

useBodyScrollLock(fullscreen)

/*
 * El sidebar se esconde con una clase en el `body`, y no subiendo el z-index de
 * la tarjeta.
 *
 * La barra lateral está en 1003 y los paneles laterales de Vuetify en 1004 y
 * 1006 —el número se lo asigna su gestor de capas al abrirlos, así que varía—.
 * Cualquier valor que dejara la tarjeta por encima del sidebar la dejaba también
 * por encima de alguno de los paneles, y abrir una actividad no mostraba nada.
 * Sacando el sidebar de la pantalla el conflicto desaparece y la tarjeta puede
 * quedarse abajo de todo lo que tiene que taparla.
 */
const FULLSCREEN_CLASS = 'bt-calendar-fullscreen'

watch(fullscreen, on => {
  document.body.classList.toggle(FULLSCREEN_CLASS, on)
})

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && fullscreen.value)
    fullscreen.value = false
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  // Salir de la ruta con la pantalla completa activa dejaría el portal sin menú.
  document.body.classList.remove(FULLSCREEN_CLASS)
})

/** Fecha del mini-calendario: saltar a marzo sin apretar "siguiente" ocho veces. */
const jumpDate = ref<Date | null>(null)

/*
 * Filtros de servicio por **exclusión** y no por inclusión: al abrir se ven
 * todas las salidas, y destildar esconde. Con una lista de inclusión habría que
 * marcar todo lo nuevo que aparezca en el rango para no perderlo de vista.
 */
const hiddenServices = ref<Set<string>>(new Set())
const hiddenCrew = ref<Set<string>>(new Set())
const showOneOff = ref(true)
const showScheduled = ref(true)

/** Clave de agrupación: las salidas sin servicio vinculado caen en una propia. */
const NO_SERVICE = '__none__'

/** Lo mismo para las salidas que todavía no tienen a nadie asignado. */
const NO_CREW = '__nocrew__'

const serviceKey = (e: CalendarEvent) => e.taxonomyCode || NO_SERVICE

/** Las claves de tripulación de una salida. Sin equipo, la clave propia. */
const crewKeys = (e: CalendarEvent) =>
  e.staff.length ? e.staff.map(s => s.providerUserId) : [NO_CREW]

/**
 * Servicios presentes en el rango cargado. Es la fuente de la leyenda de la
 * cabecera y de la lista de filtros: mostrar los 83 códigos del taxonomy en vez
 * de los cuatro que salen esta semana convertiría las dos cosas en un catálogo.
 */
const servicesInRange = computed(() => {
  const seen = new Map<string, { key: string; label: string; color: string; count: number }>()

  for (const e of events.value) {
    const key = serviceKey(e)
    const found = seen.get(key)

    if (found) {
      found.count += 1
      continue
    }

    seen.set(key, {
      key,
      label: e.taxonomyCode ? labelForOffering(e.taxonomyCode) : 'No linked service',
      color: serviceColor(e.taxonomyCode),
      count: 1,
    })
  }

  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label))
})

/** Lo que llega a la grilla después de los filtros de cliente. */
const visibleEvents = computed(() =>
  events.value.filter(e => {
    if (hiddenServices.value.has(serviceKey(e)))
      return false

    /*
     * Con **alguno** de su equipo visible, la salida se ve.
     *
     * Una salida puede llevar capitán e instructor: esconder al capitán no
     * puede llevarse puesta una salida que sigue siendo del instructor, que
     * quedó tildado. Es la misma lógica de "esto le corresponde a alguien que
     * estoy mirando", y por eso no basta con mirar el primero.
     */
    if (hiddenCrew.value.size && crewKeys(e).every(k => hiddenCrew.value.has(k)))
      return false

    return e.origin === 'manual' ? showOneOff.value : showScheduled.value
  }),
)

/* Vista activa de FullCalendar ('listDay' | 'timeGridWeek' | 'dayGridMonth'). */
const fcViewType = ref('timeGridWeek')

/*
 * La ficha del día, en PDF.
 *
 * Es **la misma planilla que la de una actividad** —roster, equipo a preparar,
 * talles, casilleros para tildar en el muelle— repetida por cada salida del día,
 * cada una en su hoja. La primera versión imprimía una tabla resumen con hora,
 * título, cupo y equipo, y eso contesta "qué sale hoy": justo lo que ya se ve en
 * la pantalla que uno está mirando. Lo que hace falta en papel es lo otro.
 *
 * Se manda lo que está A LA VISTA (`visibleEvents`), no la fecha a secas: así el
 * papel respeta los filtros del panel — "las salidas de Pedro del jueves" es
 * exactamente el caso.
 *
 * Solo en la vista Day: es donde "el día" significa algo. Un mes entero serían
 * doscientas planillas que nadie va a imprimir.
 */
const isDayView = computed(() => viewMode.value === 'calendar' && fcViewType.value === 'listDay')

const printableDay = computed(() => loadedRange.value?.from ?? '')

/** Las del día, en orden de salida. */
const printableEvents = computed(() =>
  [...visibleEvents.value].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
)

const printing = ref(false)

/**
 * 🪤 La pestaña se abre **antes** del `await`: un `window.open` posterior a una
 * espera ya no cuenta como gesto del usuario y lo mata el bloqueador de ventanas
 * emergentes (§21.7). Se navega a un enlace firmado en vez de traer el PDF por
 * `fetch`, así lo carga el navegador con su visor.
 */
const printDay = async () => {
  if (!printableEvents.value.length)
    return

  const tab = window.open('', '_blank')

  printing.value = true
  try {
    const { url } = await createEventDaySheetLink(diveCenterId, {
      date: printableDay.value,
      eventIds: printableEvents.value.map(e => e.id),
    })

    const absolute = new URL(url, apiOrigin()).toString()

    if (tab)
      tab.location.href = absolute
    else
      window.open(absolute, '_blank')
  }
  catch (e) {
    tab?.close()
    notifyError(apiMessage(e, 'Could not open the day sheet.'))
  }
  finally {
    printing.value = false
  }
}

/*
 * Las actividades tildadas, en el orden en que se ven.
 *
 * Si una deja de estar visible —por un filtro o por recargar el rango— sale
 * sola de la selección: mantener tildada una fila que ya no está en pantalla es
 * la forma más fácil de aplicar algo sin querer sobre lo que no se ve.
 */
const selectedEvents = computed(() =>
  visibleEvents.value.filter(e => selectedIds.value.includes(e.id)),
)

watch(visibleEvents, list => {
  const alive = new Set(list.map(e => e.id))

  if (selectedIds.value.some(id => !alive.has(id)))
    selectedIds.value = selectedIds.value.filter(id => alive.has(id))
})

/** Nombre por id, para la columna de equipo de la lista. */
const staffNames = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {}

  staff.value.forEach(m => { map[m.id] = `${m.firstName} ${m.lastName}`.trim() })

  return map
})

/**
 * Tripulación presente en el rango, con cuántas salidas tiene cada uno.
 *
 * Se arma desde los eventos y no desde el equipo del centro, por el mismo
 * motivo que los servicios: listar a las veinte personas de la ficha para
 * filtrar una semana en la que salen tres convierte el filtro en un directorio.
 * Si alguien dejó el equipo después de quedar asignado se cae a un rótulo
 * genérico en vez de desaparecer — su salida sigue en el calendario, así que
 * tiene que poder filtrarse.
 */
const crewInRange = computed(() => {
  const seen = new Map<string, { key: string; label: string; count: number }>()

  for (const e of events.value) {
    for (const key of crewKeys(e)) {
      const found = seen.get(key)

      if (found) {
        found.count += 1
        continue
      }

      seen.set(key, {
        key,
        label: key === NO_CREW
          ? 'No team assigned'
          : staffNames.value[key] || 'Former team member',
        count: 1,
      })
    }
  }

  // La entrada "sin equipo" al final: es la ausencia del dato, no una persona.
  return [...seen.values()].sort((a, b) =>
    (a.key === NO_CREW ? 1 : 0) - (b.key === NO_CREW ? 1 : 0)
    || a.label.localeCompare(b.label))
})

function toggleService(key: string) {
  const next = new Set(hiddenServices.value)

  next.has(key) ? next.delete(key) : next.add(key)
  hiddenServices.value = next
}

function toggleCrew(key: string) {
  const next = new Set(hiddenCrew.value)

  next.has(key) ? next.delete(key) : next.add(key)
  hiddenCrew.value = next
}

const filtersActive = computed(
  () => hiddenServices.value.size > 0
    || hiddenCrew.value.size > 0
    || !showOneOff.value
    || !showScheduled.value,
)

function clearFilters() {
  hiddenServices.value = new Set()
  hiddenCrew.value = new Set()
  showOneOff.value = true
  showScheduled.value = true
}

/*
 * Los filtros de servicio y de origen son de cliente: el rango ya está en
 * memoria y volver a pedirlo por destildar una casilla agregaría medio segundo
 * de espera a un gesto que tiene que ser instantáneo. `includeCancelled` sí es
 * de servidor —esas salidas ni siquiera vienen en la respuesta.
 */
watch(visibleEvents, list => {
  calendarOptions.value.events = list.map(toFcEvent)
})

/*
 * `eventClassNames` se evalúa al dibujar cada evento, así que cambiar la
 * selección no repinta nada por su cuenta: hay que devolverle los eventos para
 * que los vuelva a renderizar. Es la misma lista, así que no cuesta una
 * consulta.
 */
watch(selectedIds, () => {
  if (viewMode.value === 'calendar')
    calendarOptions.value.events = visibleEvents.value.map(toFcEvent)
})

/*
 * Cambiar de tema vuelve a teñir los bloques.
 *
 * El color de fondo viaja como estilo EN LÍNEA de cada evento —FullCalendar lo
 * escribe en el elemento— así que no hay hoja de estilos que lo pueda cambiar
 * sola: hay que devolverle la lista ya recalculada. Es la misma lista en
 * memoria, no cuesta una consulta.
 */
watch(isDark, () => {
  calendarOptions.value.events = visibleEvents.value.map(toFcEvent)
})

/*
 * Y cambiar las preferencias regionales, el reloj y el primer día de la semana.
 * Se mutan las propiedades sueltas por lo mismo de siempre: reemplazar el objeto
 * de opciones entero remonta el componente.
 */
watch([hour12, firstDay], ([h12, first]) => {
  calendarOptions.value.eventTimeFormat = { hour: '2-digit', minute: '2-digit', hour12: h12 }
  calendarOptions.value.slotLabelFormat = { hour: '2-digit', minute: '2-digit', hour12: h12 }
  calendarOptions.value.firstDay = first
})

/*
 * El `title` del botón sigue al estado (el ícono lo cambia el CSS). Se muta la
 * propiedad en vez de reconstruir el objeto de opciones: rehacerlo remontaría
 * el componente — la misma razón por la que `calendarOptions` es un `ref` y no
 * un `computed`.
 */
watch(fullscreen, on => {
  calendarOptions.value.customButtons!.btFullscreen.hint = on ? 'Exit full screen' : 'Full screen'
})

/** Saltar a una fecha desde el mini-calendario. */
watch(jumpDate, d => {
  if (d)
    calendarRef.value?.getApi().gotoDate(d)
})

/*
 * El rango visible lo dicta FullCalendar (`datesSet`), no nosotros: al cambiar
 * de vista o de semana nos avisa qué está mostrando y pedimos ese tramo. Así
 * la vista de mes y la de semana comparten una sola ruta de carga.
 */
const loadedRange = ref<{ from: string; to: string } | null>(null)

const isoDay = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/*
 * Contador de requests para descartar respuestas fuera de orden.
 *
 * Cambiar de vista dispara varios `datesSet` casi simultáneos (la semana, el
 * día actual, el día al que se navega) y las respuestas no vuelven en orden.
 * Sin esto, la del día de hoy —vacía, porque sus salidas ya pasaron— llegaba
 * última y borraba los eventos del día que el usuario estaba mirando.
 */
let requestSeq = 0

/**
 * Guardado de los cierres (§22).
 *
 * Se recarga el rango a la fuerza porque el backend acaba de rehacer el
 * calendario: los eventos en memoria ya no son los que hay.
 *
 * El aviso de las huérfanas es lo que evita que el provider se encuentre
 * salidas en ámbar sin saber de dónde salieron. Cerrar un día NO cancela
 * reservas: las salidas con gente anotada quedan marcadas y decide él.
 */
async function onClosuresSaved(payload: {
  closures: DiveCenterClosure[]
  detached: number
  deleted: number
  activitiesDeleted: number
  activitiesKept: number
}) {
  closures.value = payload.closures

  if (payload.activitiesDeleted > 0)
    notifySuccess(`Closed days saved. ${payload.activitiesDeleted} ${payload.activitiesDeleted === 1 ? 'activity' : 'activities'} removed.`)
  else
    notifySuccess('Closed days saved.')

  /*
   * Lo que quedó con gente anotada se avisa aparte y como información, no como
   * éxito: es lo único que le queda por resolver al provider, y mezclarlo con
   * el "guardado" lo haría pasar de largo.
   */
  const pending = payload.activitiesKept || payload.detached

  if (pending > 0) {
    notifyInfo(
      `${pending} ${pending === 1 ? 'activity has' : 'activities have'} `
      + 'bookings on a closed day and need your decision.',
    )
  }

  if (loadedRange.value)
    await loadRange(loadedRange.value.from, loadedRange.value.to, true)
}

async function loadRange(from: string, to: string, force = false) {
  /*
   * Guard de rango: `datesSet` se dispara en cada render de FullCalendar, no
   * solo al navegar. Sin este corte, cargar el rango actualizaba los eventos,
   * eso volvía a renderizar el calendario, y el nuevo render disparaba
   * `datesSet` otra vez — un bucle que dejaba el spinner girando para siempre.
   */
  if (!force && loadedRange.value?.from === from && loadedRange.value?.to === to)
    return

  const seq = ++requestSeq

  loading.value = true
  error.value = ''
  loadedRange.value = { from, to }
  try {
    const res = await listEvents(diveCenterId, { from, to, includeCancelled: includeCancelled.value })

    // Llegó tarde: ya hay una petición más nueva en curso o resuelta.
    if (seq !== requestSeq)
      return

    events.value = res.events

    const tzChanged = centerTimezone.value !== (res.timezone ?? '')

    centerTimezone.value = res.timezone ?? ''
    horizonThrough.value = res.horizonThrough ?? ''
    closures.value = res.closures ?? []
    warning.value = res.warning ?? ''

    // La sombra de hoy depende de la zona del centro, que llega con esta
    // respuesta. Recalcular acá alcanza: unos minutos de desfase no se ven, y
    // un temporizador para mover una sombra sería gastar por nada.
    refreshDayElapsed()

    /*
     * 🪤 La zona del centro llega DESPUÉS del primer dibujo, así que la primera
     * evaluación de `now` cae en la del navegador y la línea roja arranca en la
     * hora equivocada — se corregía sola recién al cambiar de vista, que es
     * cuando FullCalendar vuelve a preguntar.
     *
     * Se le devuelve la opción con una identidad de función nueva: es lo que el
     * envoltorio de Vue mira para saber que cambió, y con eso reposiciona la
     * línea sin remontar el calendario.
     */
    if (tzChanged)
      calendarOptions.value.now = () => centerNow()

    /*
     * Mismo recurso para los cierres (§22): `dayCellClassNames` y
     * `dayCellDidMount` se cierran sobre `closedDays`, pero el envoltorio de
     * Vue mira la IDENTIDAD de la opción, no lo que la función lee por dentro.
     * Sin esto, los cierres que llegan con la respuesta no se pintan hasta que
     * el usuario cambia de vista.
     */
    calendarOptions.value.dayCellClassNames = arg =>
      closedDays.value.has(isoDay(arg.date)) ? ['bt-day--closed'] : []

    calendarOptions.value.dayCellDidMount = arg => {
      const date = isoDay(arg.date)

      if (closedDays.value.has(date))
        arg.el.title = `Closed${closedSuffix(date)}`
    }

    // Se muta la propiedad en vez de rehacer el objeto de opciones: reemplazarlo
    // entero reinicializa el componente, que es la otra mitad del bucle.
    calendarOptions.value.events = visibleEvents.value.map(toFcEvent)

    announceTimezone()

    /*
     * La actividad del enlace directo, ya con el rango en memoria.
     *
     * Se resuelve acá y no en `onMounted` porque hasta que la respuesta no
     * llega no hay contra qué buscar el id. La bandera se limpia pase lo que
     * pase: si esa salida se canceló o se borró entre que se copió el enlace y
     * se abrió, el calendario se muestra igual en su semana y no queda
     * intentando abrir un panel que no existe en cada recarga del rango.
     */
    if (pendingEventId.value) {
      const target = res.events.find(e => e.id === pendingEventId.value)

      pendingEventId.value = ''

      if (target)
        openDetail(target)
    }
  }
  catch (e) {
    if (seq === requestSeq)
      error.value = e instanceof ApiError ? e.message : 'Could not load the calendar.'
  }
  finally {
    // Solo la petición vigente apaga el spinner, o una respuesta vieja lo
    // apagaría mientras la nueva sigue en vuelo.
    if (seq === requestSeq)
      loading.value = false
  }
}

onMounted(async () => {
  // Los tres alimentan los formularios, no la grilla: si alguno falla el
  // calendario se sigue viendo. Por eso van en paralelo y con catch propio.
  const [dc, offs, team] = await Promise.allSettled([
    getDiveCenter(diveCenterId),
    listOfferings(diveCenterId),
    listTeam(),
  ])

  if (dc.status === 'fulfilled')
    diveCenter.value = dc.value.diveCenter
  if (offs.status === 'fulfilled')
    offerings.value = offs.value.offerings
  if (team.status === 'fulfilled') {
    /*
     * Se acota a quien trabaja en este centro. owner y admin ven todos los
     * centros por rol, así que entran siempre; a quien todavía no tiene ninguno
     * asignado también se lo deja pasar — es una ficha válida del equipo, y
     * esconderla sin explicación es peor que ofrecerla de más.
     */
    staff.value = team.value.members.filter(m =>
      m.role === 'owner'
      || m.role === 'admin'
      || !m.assignedDiveCenterIds?.length
      || m.assignedDiveCenterIds.includes(diveCenterId),
    )
  }
})

// ---- Alta ----
/**
 * Abre el alta. El aviso de día cerrado NO va acá sino en `submitCreate`: desde
 * "New Activity" la fecha se elige dentro del panel, así que al abrirlo todavía
 * no hay nada que comprobar.
 */
function startCreate(date?: string, time?: string) {
  /*
   * Pasado el cupo del plan no se programan salidas nuevas. La guarda va acá y
   * no solo en el botón porque al alta también se llega tocando un hueco de la
   * grilla, y ahí no hay nada que deshabilitar (§33).
   */
  if (!quotaGuard()) return

  editorDate.value = date ?? ''
  editorTime.value = time ?? ''
  editorError.value = ''
  showDetail.value = false
  showEditor.value = true
}

async function submitCreate(payload: CreateEventPayload, clients: DiveCenterClient[] = []) {
  editorError.value = ''

  /*
   * El aviso de día cerrado va acá, al GUARDAR, y no al abrir el formulario.
   *
   * Desde "New Activity" la fecha se elige adentro del panel, así que al abrirlo
   * todavía no hay nada que comprobar: chequear ahí solo cubría el alta rápida
   * clickeando una celda, que además ya está bloqueada. Este es el único punto
   * por el que pasan los dos caminos.
   *
   * Avisa, no bloquea: programar algo un día cerrado es legítimo —para eso
   * existe el evento puntual— pero tiene que ser una decisión consciente.
   */
  if (closedDays.value.has(payload.date)) {
    const ok = await confirmRef.value?.open({
      title: 'The centre is closed that day',
      message: `${payload.date} is marked as closed${closedSuffix(payload.date)}. Nothing from `
        + 'your schedule runs that day. Add this activity anyway?',
      confirmText: 'Add anyway',
    })

    if (!ok)
      return
  }

  submitting.value = true
  try {
    const res = await createEvent(diveCenterId, payload)

    /*
     * Las reservas van **después** del alta y no en el mismo payload: no son
     * parte del evento sino de él, y hasta que no existe no hay contra qué
     * crearlas. En paralelo, porque el cupo lo resuelve el backend de forma
     * atómica por reserva.
     *
     * Si alguna falla, el evento ya quedó creado — se avisa cuántas entraron y
     * el provider completa desde el panel, que es mejor que deshacer el alta.
     */
    let booked = 0

    if (clients.length) {
      const results = await Promise.allSettled(clients.map(c =>
        createBooking(diveCenterId, res.event.id, { clientId: c.id, seats: 1 }),
      ))

      booked = results.filter(r => r.status === 'fulfilled').length
      if (booked < clients.length)
        notifyError(`Activity created, but only ${booked} of ${clients.length} bookings went through.`)
    }

    showEditor.value = false
    notifySuccess(booked ? `Activity added with ${booked} booked.` : 'Activity added.')
    reload()
  }
  catch (e) {
    editorError.value = e instanceof ApiError ? e.message : 'Could not add the activity.'
  }
  finally {
    submitting.value = false
  }
}

// ---- Edición del detalle ----
async function applyUpdate(
  payload: UpdateEventPayload,
  successMessage: string,
  { close = false } = {},
) {
  if (!selected.value)
    return

  detailError.value = ''
  submitting.value = true
  try {
    const res = await updateEvent(diveCenterId, selected.value.id, payload)

    // Se refresca el seleccionado además de la grilla: cuando el panel queda
    // abierto (cancelar, reactivar) tiene que mostrar lo que acaba de guardarse.
    selected.value = res.event
    notifySuccess(successMessage)
    reload()

    /*
     * Guardar cierra el panel. Dejarlo abierto obligaba a cerrarlo a mano para
     * volver a ver la semana, y sin nada que cambiara en pantalla no quedaba
     * claro si el guardado había ocurrido. Cancelar y reactivar no cierran: ahí
     * el estado nuevo **es** lo que hay que ver.
     */
    if (close)
      showDetail.value = false
  }
  catch (e) {
    detailError.value = e instanceof ApiError ? e.message : 'Could not save the activity.'
  }
  finally {
    submitting.value = false
  }
}

/**
 * El roster cambió: hay que releer la salida seleccionada y la grilla, porque
 * la ocupación que muestran quedó vieja.
 */
async function onRosterChanged() {
  reload()
  if (!selected.value)
    return

  try {
    const res = await getEvent(diveCenterId, selected.value.id)

    selected.value = res.event
  }
  catch { /* la grilla ya se recargó; el panel se corrige al reabrirlo */ }
}

/*
 * La tanda masiva cambió ocupación y equipo de varias actividades: se relee el
 * rango. La selección se conserva a propósito — es normal aplicar dos cosas
 * seguidas sobre el mismo grupo (anotar gente y después asignar al capitán), y
 * volver a tildar seis filas para eso sería trabajo de más.
 */
/*
 * Se aplicó algo desde el panel Manage.
 *
 * La selección se limpia al CERRAR el panel y no al aplicar: cuando hay
 * rechazos por cupo el panel queda abierto para poder forzarlos, y ese
 * reintento vuelve a mandar **las mismas actividades** — vaciarla antes lo
 * dejaría sin nada que reintentar.
 */
const bulkApplied = ref(false)

function onBulkApplied() {
  bulkApplied.value = true
  reload()
}

watch(showBulk, open => {
  if (open || !bulkApplied.value)
    return

  bulkApplied.value = false

  /*
   * Lo aplicado ya no está pendiente. Dejar las mismas actividades tildadas
   * invita a repetir la acción sobre gente que ya quedó anotada, y el botón
   * Manage se queda habilitado como si faltara algo por hacer. Se sale también
   * del modo selección: se entró para hacer esto, y ya está hecho.
   */
  selectedIds.value = []
  selectMode.value = false
})

/**
 * Guardar los cambios de una actividad.
 *
 * Avisa si se la está MOVIENDO a un día cerrado. Solo cuando la fecha cambia:
 * una actividad que ya vivía en un día cerrado no tiene que pedir confirmación
 * cada vez que se le toca el cupo o el equipo — el aviso dejaría de leerse.
 *
 * Es el mismo criterio que el alta (`submitCreate`): avisa, no bloquea.
 */
const onSave = async (payload: UpdateEventPayload) => {
  const movedTo = payload.date && payload.date !== selected.value?.date
    ? payload.date
    : null

  if (movedTo && closedDays.value.has(movedTo)) {
    const ok = await confirmRef.value?.open({
      title: 'The centre is closed that day',
      message: `${movedTo} is marked as closed${closedSuffix(movedTo)}. Nothing from your `
        + 'schedule runs that day. Move this activity there anyway?',
      confirmText: 'Move anyway',
    })

    if (!ok)
      return
  }

  await applyUpdate(payload, 'Activity updated.', { close: true })
}

const onCancelDeparture = (reason: string) =>
  applyUpdate({ status: 'cancelled', cancelReason: reason }, 'Activity cancelled.')

const onReactivate = () => applyUpdate({ status: 'scheduled' }, 'Activity reactivated.')

async function onDelete() {
  if (!selected.value)
    return

  const ok = await confirmRef.value?.open({
    title: 'Delete activity',
    message: `Delete "${selected.value.title}" on ${selected.value.date} at ${selected.value.time}? This cannot be undone.`,
    confirmText: 'Delete',
    confirmColor: 'error',
  })
  if (!ok)
    return

  submitting.value = true
  try {
    await deleteEvent(diveCenterId, selected.value.id)
    showDetail.value = false
    notifySuccess('Activity deleted.')
    reload()
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not delete the activity.'))
  }
  finally {
    submitting.value = false
  }
}

const onDatesSet = (arg: DatesSetArg) => {
  // `end` de FullCalendar es exclusivo; el backend espera un rango inclusive.
  const to = new Date(arg.end.getTime() - 86_400_000)

  // Qué vista está mirando: lo necesita el botón de imprimir, que solo tiene
  // sentido sobre un día. `datesSet` es el único lugar donde FullCalendar lo
  // cuenta sin tener que preguntarle a la instancia.
  fcViewType.value = arg.view.type

  loadRange(isoDay(arg.start), isoDay(to))
}

/*
 * "Done" cierra el gesto entero: sale del modo Y limpia lo tildado.
 *
 * Se probó conservando la selección —para poder salir, mirar una salida y
 * volver— y en la práctica confundía: el botón decía "Select" como si no
 * hubiera nada elegido, pero la barra seguía abajo con tres marcadas y el botón
 * de editar activo. Dos mensajes opuestos sobre el mismo estado. Salir de un
 * modo tiene que dejar la pantalla como estaba antes de entrar; para vaciar sin
 * salir está "Clear".
 */
function toggleSelectMode() {
  selectMode.value = !selectMode.value

  if (!selectMode.value)
    selectedIds.value = []
}

/** Abre el panel de detalle de una actividad. Lo usan la grilla y la lista. */
function openDetail(ev: CalendarEvent) {
  selected.value = ev
  // Los paneles viven en la misma posición: abrir uno cierra los otros, o se
  // superponen.
  showEditor.value = false
  showBulk.value = false
  showDetail.value = true
}

const onEventClick = (arg: EventClickArg) => {
  const ev = arg.event.extendedProps.bt as CalendarEvent

  if (!selectMode.value) {
    openDetail(ev)

    return
  }

  // Cancelada = no admite reservas, así que tampoco se tilda. Mismo criterio
  // que la lista.
  if (ev.status === 'cancelled')
    return

  selectedIds.value = selectedIds.value.includes(ev.id)
    ? selectedIds.value.filter(id => id !== ev.id)
    : [...selectedIds.value, ev.id]
}

/*
 * `ref` y no `computed`: el wrapper de FullCalendar observa este objeto, y
 * devolver uno nuevo en cada cambio de eventos lo remonta. Las opciones se
 * crean una sola vez y solo se muta `events`.
 */
const calendarOptions = ref<CalendarOptions>({
  plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
  initialView: 'timeGridWeek',
  // Vacío = hoy, que es el comportamiento por defecto de la librería.
  ...(initialDateParam ? { initialDate: initialDateParam } : {}),
  headerToolbar: {
    start: 'btPanel btFullscreen prev,next today',
    center: 'title',
    end: 'listDay,timeGridWeek,dayGridMonth',
  },
  buttonText: { today: 'Today', week: 'Week', month: 'Month', list: 'Day' },

  /*
   * El disparador del panel vive en la barra de FullCalendar y no al lado del
   * título de la página: navegar por fechas y filtrar son la misma tarea que
   * "prev / next / today", y separarlos obligaba a mover el ojo entre dos
   * grupos de controles que hacen lo mismo.
   */
  customButtons: {
    // El ícono va por CSS (ver la hoja de estilos): FullCalendar escribe el
    // rótulo como texto plano y no admite markup, así que un `::before` es la
    // única forma de acompañarlo con un ícono. `hint` termina en el `title`.
    btPanel: {
      text: 'Filters',
      hint: 'Filters and date navigation',
      click: () => { showPanel.value = !showPanel.value },
    },
    btFullscreen: {
      text: '',
      hint: 'Full screen',
      click: () => { fullscreen.value = !fullscreen.value },
    },
  },

  /*
   * `local` y no una zona con nombre: los eventos se le pasan como hora de
   * pared del centro sin offset, así que FullCalendar los dibuja tal cual sin
   * convertir nada. Ver `useCalendarEvents.ts`.
   */
  timeZone: 'local',

  /*
   * Marca visual de lo seleccionado. Es una clase y no un color de evento
   * porque el color ya codifica el servicio (§20.8): pisarlo para marcar la
   * selección perdería justo el dato que hace escaneable la grilla.
   */
  eventClassNames: arg =>
    selectedIds.value.includes((arg.event.extendedProps.bt as CalendarEvent).id)
      ? ['bt-event--picked']
      : [],

  /*
   * Días cerrados por el centro (§22).
   *
   * Se marcan con una clase sobre la CELDA y no pintando eventos: en un día
   * cerrado justamente no hay eventos que pintar, y sin la marca el día se ve
   * igual que uno sin salidas programadas. Es el mismo recurso que la sombra
   * del pasado (§20.27), que también tiñe el lienzo por debajo.
   */
  dayCellClassNames: arg =>
    closedDays.value.has(isoDay(arg.date)) ? ['bt-day--closed'] : [],

  // El motivo va en el `title` de la celda: un día apagado sin explicación se
  // lee como un bug del calendario.
  dayCellDidMount: arg => {
    const date = isoDay(arg.date)

    if (closedDays.value.has(date))
      arg.el.title = `Closed${closedSuffix(date)}`
  },

  // Primer día de la semana, según Preferences.
  firstDay: firstDay.value,
  height: 'auto',

  /*
   * La línea roja de "ahora", como la de Google Calendar.
   *
   * 🪤 FullCalendar la dibuja con el reloj del NAVEGADOR, y esta grilla está en
   * hora del CENTRO: con zonas distintas la línea apuntaba a una hora que no
   * existe en este calendario, así que estaba apagada en ese caso. Se resuelve
   * dándole un `now` propio —el reloj del centro expresado como hora local del
   * navegador, que es exactamente cómo se dibujan los eventos— en vez de
   * renunciar a la línea.
   *
   * `now` es una FUNCIÓN y no un valor: FullCalendar la vuelve a llamar para
   * mover la línea, y con un `Date` fijo se quedaría clavada en la hora en que
   * se cargó la página. De paso arregla `fc-day-today`, que con zonas cruzadas
   * podía marcar la columna equivocada.
   */
  nowIndicator: true,
  now: () => centerNow(),

  /*
   * Fila de "todo el día" ENCENDIDA: es donde caen las salidas de varios días
   * (un curso de tres jornadas). Como bloque de la grilla taparían la semana
   * entera — ver `MULTI_DAY_MINUTES` en `useCalendarEvents`.
   */
  allDaySlot: true,
  allDayText: 'Multi-day',

  slotMinTime: `${String(SLOT_MIN_HOUR).padStart(2, '0')}:00:00`,
  slotMaxTime: `${String(SLOT_MAX_HOUR).padStart(2, '0')}:00:00`,
  slotDuration: '01:00:00',
  expandRows: true,

  /*
   * Salidas simultáneas LADO A LADO, no encimadas.
   *
   * Por defecto FullCalendar las superpone con un desplazamiento, y con tres
   * offerings saliendo a las 09:00 el resultado es una pila de bloques
   * cortados donde no se lee ni el título ni la ocupación. Repartidas a lo
   * ancho cada una conserva su columna.
   */
  slotEventOverlap: false,
  // Más de tres en la misma franja no entran legibles: el resto se resume en
  // un "+N" que despliega el día.
  eventMaxStack: 3,
  // Lo mismo en la vista de mes, por celda.
  dayMaxEvents: 4,

  /*
   * En la vista de mes, las actividades con hora también van como BLOQUE.
   *
   * Por defecto FullCalendar las dibuja ahí como "dot event": un puntito y
   * texto suelto, sin fondo. El color del servicio viaja en `backgroundColor` y
   * ese formato lo ignora, así que justo en la vista donde más se necesita
   * distinguir de un golpe —un mes entero— las de horario salían todas iguales,
   * mientras las de varios días (que ya son bloques) sí tenían su color. De
   * paso, el texto tomaba el color por defecto y la barra de cupo, que se pinta
   * con `currentcolor`, salía negra.
   *
   * Va global y no bajo `views.dayGridMonth` —donde parecería su lugar— porque
   * ahí FullCalendar lo ignora. En las vistas de horario no cambia nada: los
   * eventos con hora ya son bloques.
   */
  eventDisplay: 'block',
  eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: hour12.value },
  slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: hour12.value },
  noEventsText: 'No activities in this range.',

  /*
   * Encabezado de columna: día de la semana y número, sin el mes.
   *
   * El default de FullCalendar para la semana es "Wed 8/26", y ese mes está
   * repetido: el título de la barra, dos centímetros más arriba, ya dice de qué
   * mes es la semana que se está mirando. Peor todavía, "8/26" se lee al revés
   * en media Europa, así que aporta ambigüedad además de ruido. Queda "Wed 26".
   *
   * Va bajo `views` y no suelto: en la vista de mes el encabezado es solo el
   * nombre del día ("Mon", "Tue") y agregarle el número lo rompería.
   */
  views: {
    timeGridWeek: { dayHeaderFormat: { weekday: 'short', day: 'numeric' } },
  },

  events: [],
  datesSet: onDatesSet,
  eventClick: onEventClick,

  // Click en un hueco del calendario = agregar una salida ese día, con la
  // fecha ya puesta. Es el gesto que uno espera de un calendario.
  /*
   * Click en una celda: se abre el alta con esa fecha **y esa hora**.
   *
   * `dateStr` viene como '2026-08-11T07:00:00' en las vistas de horario y como
   * '2026-08-11' en la de mes y en la fila de varios días. Antes se recortaba a
   * los diez primeros caracteres y la hora se perdía: apuntarle a la celda de
   * las 07:00 abría el formulario vacío y había que volver a elegirla, que es
   * justo lo que el gesto ya había dicho.
   *
   * `allDay` distingue los dos casos sin adivinar por el largo de la cadena.
   */
  /*
   * Click en una celda = alta rápida en ese día y hora. En un día cerrado no
   * hace nada: el gesto es demasiado fácil de disparar sin querer como para que
   * abra un formulario sobre un día en el que no sale nada. Para programar algo
   * ahí igual está el botón de alta, que sí pregunta.
   */
  dateClick: (arg: { dateStr: string; allDay: boolean }) => {
    const date = arg.dateStr.slice(0, 10)

    if (closedDays.value.has(date)) {
      notifyInfo(`${date} is closed${closedSuffix(date)}.`)

      return
    }

    startCreate(date, arg.allDay ? undefined : arg.dateStr.slice(11, 16))
  },
})

/**
 * Aviso de desfase: el provider puede estar en Madrid administrando un centro
 * en Bonaire. Los horarios que ve son los del centro, y eso hay que decirlo —
 * si no, anota una salida a la hora equivocada y se entera cuando el buzo no
 * aparece.
 */
const tzMismatch = computed(() => {
  const browser = browserTimezone()

  return !!centerTimezone.value && !!browser && centerTimezone.value !== browser
})

/*
 * El desfase se avisa **una vez por sesión del navegador**, en un snackbar largo.
 *
 * Era un cartel fijo arriba de la grilla, y ahí estaba mal por dos motivos: se
 * comía dos líneas de alto en la pantalla donde el alto es todo, y a la tercera
 * carga ya nadie lo lee. Después pasó a avisarse una vez por visita, y tampoco:
 * la página se vuelve a montar cada vez que se entra al calendario —y el
 * `RouterView` la remonta al cambiar de centro—, así que en una mañana de
 * trabajo el mismo cartel salía diez veces.
 *
 * `sessionStorage` y no `localStorage`: es un dato de contexto que conviene
 * recordar al empezar a trabajar, y una pestaña nueva es exactamente eso. La
 * clave incluye el centro y las dos zonas, así que cambiar de centro —o viajar
 * a otro huso— vuelve a avisar en vez de quedarse callado con un dato distinto.
 */
const tzNoticeKey = computed(
  () => `bt.tzNotice.${diveCenterId}.${centerTimezone.value}>${browserTimezone()}`,
)

function announceTimezone() {
  if (!tzMismatch.value)
    return

  try {
    if (sessionStorage.getItem(tzNoticeKey.value))
      return

    sessionStorage.setItem(tzNoticeKey.value, '1')
  }
  catch {
    // Sin `sessionStorage` (modo privado en algunos navegadores) se avisa
    // igual: repetir el cartel es molesto, esconderlo sería peor.
  }

  notifyInfo(
    `Times are shown in ${centerTimezone.value} — the dive center's time zone. `
    + `Your device is in ${browserTimezone()}.`,
    20_000,
  )
}

/*
 * Las métricas cuentan lo que se está VIENDO, no todo el rango: con un filtro
 * puesto, una cabecera que sigue diciendo 23 salidas mientras la grilla muestra
 * tres deja al provider sin saber cuál de los dos números creer.
 */
/*
 * El rango que se está mirando arranca más allá del horizonte generado.
 *
 * Solo se avisa cuando TODO el rango cae afuera: un mes que lo cruza por la
 * mitad muestra sus salidas hasta donde llegan, y ahí el cartel sobraría.
 */
const beyondHorizon = computed(
  () => !!horizonThrough.value
    && !!loadedRange.value
    && loadedRange.value.from > horizonThrough.value,
)

const stats = computed(() => {
  const list = visibleEvents.value.filter(e => e.status !== 'cancelled')
  const seats = list.reduce((acc, e) => acc + (e.capacity ?? 0), 0)
  const booked = list.reduce((acc, e) => acc + e.booked, 0)

  return {
    departures: list.length,
    cancelled: visibleEvents.value.length - list.length,
    detached: visibleEvents.value.filter(e => e.detached).length,
    booked,
    seats,
  }
})

const reload = () => {
  if (loadedRange.value)
    loadRange(loadedRange.value.from, loadedRange.value.to, true)
}
</script>

<template>
  <div>
    <!-- Sin botón de alta acá: vive en la barra de la tarjeta, junto a Select
         y al conmutador de vistas, donde sigue estando en pantalla completa. -->
    <AlertBox
      variant="danger"
      :message="error"
    />

    <VAlert
      v-if="warning === 'no-timezone'"
      type="warning"
      variant="tonal"
      density="comfortable"
      class="mb-4"
    >
      This dive center has no time zone set, so no activities can be scheduled.
      <RouterLink :to="{ name: 'dive-centers-id-profile-edit', params: { id: diveCenterId } }">
        Set it in the profile
      </RouterLink>
      to start using the calendar.
    </VAlert>

    <VAlert
      v-if="stats.detached"
      type="warning"
      variant="tonal"
      density="comfortable"
      class="mb-4"
    >
      {{ stats.detached }} activity{{ stats.detached > 1 ? 'ies' : '' }} in this range
      {{ stats.detached > 1 ? 'are' : 'is' }} no longer produced by your schedule but
      {{ stats.detached > 1 ? 'have' : 'has' }} bookings. Open
      {{ stats.detached > 1 ? 'them' : 'it' }} to cancel or keep
      {{ stats.detached > 1 ? 'them' : 'it' }}.
    </VAlert>

    <VRow class="mb-1">
      <VCol
        v-for="s in [
          { label: 'Activities', value: stats.departures, icon: 'ri-anchor-line' },
          { label: 'Seats booked', value: `${stats.booked}/${stats.seats}`, icon: 'ri-group-line' },
          { label: 'Cancelled', value: stats.cancelled, icon: 'ri-close-circle-line' },
          { label: 'Needs review', value: stats.detached, icon: 'ri-error-warning-line' },
        ]"
        :key="s.label"
        cols="6"
        md="3"
      >
        <VCard
          border
          flat
        >
          <VCardText class="d-flex align-center ga-3 py-3">
            <VAvatar
              size="36"
              rounded
              color="primary"
              variant="tonal"
            >
              <VIcon :icon="s.icon" />
            </VAvatar>
            <div>
              <div class="text-h6 lh-1">
                {{ s.value }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ s.label }}
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!--
      Más allá del horizonte no hay nada generado, y un mes vacío sin explicación
      se lee como que el horario se rompió.
    -->
    <VAlert
      v-if="beyondHorizon"
      type="info"
      variant="tonal"
      density="compact"
      class="mt-3"
    >
      Your schedule is generated up to {{ horizonThrough }}. Activities further out
      appear as that date gets closer — nothing is missing.
    </VAlert>

    <VCard
      border
      flat
      class="mt-3 bt-calendar-card"
      :class="{
        'bt-calendar-card--fullscreen': fullscreen,
        'bt-panel-open': showPanel,
      }"
      :style="{ '--bt-day-elapsed': dayElapsed }"
    >
      <VProgressLinear
        v-if="loading"
        indeterminate
        color="primary"
      />

      <!--
        Leyenda de colores. Va fuera del panel plegable a propósito: el color es
        lo primero que se lee de la grilla, así que su clave tiene que estar
        siempre a la vista. Los filtros, que son un gesto ocasional, sí se
        pliegan.
      -->
      <!--
        Sin leyenda de colores: las casillas de Services del panel ya llevan el
        color de cada servicio, así que la tira de arriba repetía la clave y se
        comía dos renglones de alto. El panel ahora arranca abierto, con lo cual
        esa clave está a la vista igual.
      -->
      <div class="d-flex align-start ga-4 px-4 pt-4 bt-calendar-header">
        <VSpacer />

        <!--
          Alta, modo selección y conmutador de vistas, en una sola fila y a la
          misma altura: son los tres controles de la pantalla y repartirlos
          entre la cabecera, el panel y la barra obligaba a buscarlos en tres
          lugares distintos.
        -->
        <div class="d-flex align-center flex-wrap ga-2 bt-calendar-actions">
          <VBtn
            color="primary"
            prepend-icon="ri-add-line"
            :disabled="warning === 'no-timezone' || quotaBlocked"
            @click="startCreate()"
          >
            New activity
          </VBtn>

          <!-- Solo en la grilla: en la lista las casillas ya están siempre. -->
          <VBtn
            v-if="viewMode === 'calendar'"
            :color="selectMode ? 'primary' : undefined"
            :variant="selectMode ? 'flat' : 'outlined'"
            prepend-icon="ri-checkbox-multiple-line"
            @click="toggleSelectMode"
          >
            {{ selectMode ? 'Done' : 'Select' }}
          </VBtn>

          <!--
            Imprimir, solo en la vista Day. En semana o mes el botón existiría
            para dar una hoja que nadie puede usar.
          -->
          <VBtn
            v-if="isDayView"
            variant="outlined"
            color="default"
            prepend-icon="ri-printer-line"
            :disabled="!printableEvents.length"
            :loading="printing"
            title="Activity sheets for this day (PDF)"
            @click="printDay"
          >
            Print
          </VBtn>

          <!--
            Calendario o lista. `VBtnGroup` y no `VBtnToggle`: fuera de un
            `VForm` el toggle es un `VSlideGroup` por dentro y dibuja su barra
            de desplazamiento debajo de los botones (§21.7).
          -->
          <VBtnGroup
            variant="outlined"
            divided
          >
            <VBtn
              :color="viewMode === 'calendar' ? 'primary' : undefined"
              :variant="viewMode === 'calendar' ? 'flat' : 'outlined'"
              prepend-icon="ri-calendar-2-line"
              @click="viewMode = 'calendar'"
            >
              Calendar
            </VBtn>
            <VBtn
              :color="viewMode === 'list' ? 'primary' : undefined"
              :variant="viewMode === 'list' ? 'flat' : 'outlined'"
              prepend-icon="ri-list-check"
              @click="viewMode = 'list'"
            >
              List
            </VBtn>
          </VBtnGroup>
        </div>
      </div>

      <!--
        Barra de selección, compartida por las dos vistas. En la grilla explica
        además qué hace el modo: sin eso, entrar en modo selección y que el
        click deje de abrir la actividad se lee como que algo se rompió.
      -->
      <VExpandTransition>
        <!--
          A la derecha, debajo de su propio botón: la barra es la consecuencia
          de haber tocado Select, y alineada a la izquierda quedaba a media
          pantalla de distancia del gesto que la abre.
        -->
        <div
          v-if="selectMode || selectedIds.length"
          class="d-flex align-center justify-end flex-wrap ga-2 px-4 pt-3"
        >
          <span class="text-body-2 font-weight-medium">
            {{ selectedIds.length }} selected
          </span>
          <span
            v-if="viewMode === 'calendar' && !selectedIds.length"
            class="text-caption text-medium-emphasis"
          >
            Click the activities you want to work on.
          </span>
          <VBtn
            size="small"
            color="primary"
            prepend-icon="ri-user-add-line"
            :disabled="!selectedIds.length"
            @click="showBulk = true"
          >
            Edit activities
          </VBtn>
          <VBtn
            v-if="selectedIds.length"
            size="small"
            variant="text"
            @click="selectedIds = []"
          >
            Clear
          </VBtn>
        </div>
      </VExpandTransition>

      <VCardText class="d-flex align-start ga-4">
        <!--
          Panel de navegación y filtros. `v-show` y no `v-if`: el mini-calendario
          conserva el mes en el que quedó al plegarlo, y remontarlo lo devolvía
          siempre al actual.
        -->
        <div
          v-show="showPanel"
          class="bt-calendar-panel flex-shrink-0"
        >
          <!-- Lunes primero, como la grilla grande (`firstDay: 1`). -->
          <VDatePicker
            v-model="jumpDate"
            hide-header
            show-adjacent-months
            :first-day-of-week="1"
            width="100%"
            class="mb-4 bt-mini-calendar"
          />

          <!--
            Los cierres van ARRIBA de los filtros: contestan "¿qué días no
            trabajamos?", que es una pregunta sobre el calendario y no una forma
            de mirarlo. Y viven acá y no en el wizard del perfil porque son
            operativos y desde el calendario el efecto se ve en el acto (§22).
          -->
          <div class="text-overline text-medium-emphasis mb-1">
            Closed days
          </div>

          <p
            v-if="!closures.length"
            class="text-caption text-medium-emphasis mb-2"
          >
            Holidays and time off — they close the day for every offer.
          </p>

          <!--
            Listado y no solo un contador: el número no dice CUÁLES son, que es
            lo que uno viene a comprobar mirando la semana. Cada uno abre el
            panel de edición.
          -->
          <!--
            Cada cierre en su propio recuadro: en una lista plana se leían como
            texto suelto del panel y no como elementos que se pueden abrir.
          -->
          <VCard
            v-for="c in closures"
            v-else
            :key="c.id || `${c.from}_${c.to}`"
            variant="outlined"
            class="mb-2"
            :class="{ 'bt-closure-chip': canConfigureCenter }"
            @click="canConfigureCenter && openClosure(c)"
          >
            <!--
              `text-disabled` y no `text-medium-emphasis`: el gris de referencia
              es el del placeholder de un campo outlined (alfa 0.4). El de
              énfasis medio es 0.7 y sobre blanco se lee casi negro al lado del
              borde claro de la tarjeta.
            -->
            <VCardText class="py-2 px-3">
              <div class="text-caption font-weight-medium text-disabled">
                {{ c.from === c.to ? c.from : `${c.from} → ${c.to}` }}
              </div>
              <div
                v-if="c.reason"
                class="text-caption text-disabled"
              >
                {{ c.reason }}
              </div>
            </VCardText>
          </VCard>

          <VBtn
            v-if="canConfigureCenter"
            size="small"
            variant="outlined"
            color="primary"
            prepend-icon="ri-add-line"
            block
            @click="openClosure(null)"
          >
            Add closed day
          </VBtn>

          <VDivider class="my-3" />

          <div class="d-flex align-center justify-space-between mb-1">
            <div class="text-overline text-medium-emphasis">
              Filters
            </div>
            <VBtn
              v-if="filtersActive"
              size="x-small"
              variant="text"
              @click="clearFilters"
            >
              Reset
            </VBtn>
          </div>

          <VCheckbox
            v-model="showScheduled"
            label="From schedule"
            density="compact"
            hide-details
          />
          <VCheckbox
            v-model="showOneOff"
            label="One-off"
            density="compact"
            hide-details
          />
          <VCheckbox
            v-model="includeCancelled"
            label="Cancelled"
            density="compact"
            hide-details
            @update:model-value="reload"
          />


          <VDivider class="my-3" />

          <div class="text-overline text-medium-emphasis mb-1">
            Services
          </div>
          <p
            v-if="!servicesInRange.length"
            class="text-caption text-medium-emphasis mb-0"
          >
            Nothing scheduled in this range.
          </p>
          <!--
            El color del servicio va EN la casilla, no en un punto al lado: dos
            marcas de color para el mismo dato compiten entre sí, y la casilla ya
            está ahí. Es el patrón de "Event Filters" de la plantilla.
          -->
          <VCheckbox
            v-for="svc in servicesInRange"
            :key="svc.key"
            :model-value="!hiddenServices.has(svc.key)"
            :color="svc.color"
            density="compact"
            hide-details
            @update:model-value="toggleService(svc.key)"
          >
            <template #label>
              <span class="d-flex align-center ga-2">
                <span class="text-truncate">{{ svc.label }}</span>
                <span class="text-disabled">{{ svc.count }}</span>
              </span>
            </template>
          </VCheckbox>

          <VDivider class="my-3" />

          <!--
            Tripulación. Contesta "¿qué hace Pedro esta semana?", que es la
            pregunta con la que se arma un turno y la única que no se puede
            responder mirando la grilla: el equipo asignado no entra en el
            bloque de una salida.
          -->
          <div class="text-overline text-medium-emphasis mb-1">
            Team
          </div>
          <p
            v-if="!crewInRange.length"
            class="text-caption text-medium-emphasis mb-0"
          >
            Nothing scheduled in this range.
          </p>
          <VCheckbox
            v-for="member in crewInRange"
            :key="member.key"
            :model-value="!hiddenCrew.has(member.key)"
            density="compact"
            hide-details
            @update:model-value="toggleCrew(member.key)"
          >
            <template #label>
              <span class="d-flex align-center ga-2">
                <span class="text-truncate">{{ member.label }}</span>
                <span class="text-disabled">{{ member.count }}</span>
              </span>
            </template>
          </VCheckbox>
        </div>

        <!--
          La lista es la otra cara del mismo rango: comparte datos, filtros y
          métricas con la grilla, y es donde se opera sobre varias actividades a
          la vez. La barra de selección va arriba de la tabla, no flotando: es
          donde está el gesto que la produce.
        -->
        <div
          v-if="viewMode === 'list'"
          class="flex-grow-1"
        >
          <ActivityListView
            v-model:selected="selectedIds"
            :events="visibleEvents"
            :staff-names="staffNames"
            :loading="loading"
            @open="openDetail"
          />
        </div>

        <FullCalendar
          v-else
          ref="calendarRef"
          :options="calendarOptions"
          class="bt-calendar flex-grow-1"
        >
          <template #eventContent="{ event }">
            <!--
              Render propio: lo que importa de una salida es la OCUPACIÓN, no su
              título. El render por defecto muestra hora y texto, y tres
              offerings saliendo a la misma hora quedan ilegibles.
            -->
            <!--
              🪤 El color va como estilo EN LÍNEA y no heredado.
              
              En las vistas de grilla FullCalendar pinta el bloque con el
              `textColor` del evento y alcanzaría con heredar. En la vista de
              lista NO: ahí el color del evento vive solo en el puntito, y el
              contenido cae dentro de `.fc-list-event-title`, que la plantilla
              fija a medium emphasis (`full-calendar.scss`). O sea que la hora, el
              título y la ocupación salían en gris claro sobre blanco — que es
              justo lo que no se leía. Ponerlo acá lo resuelve en las tres vistas
              con una sola regla.
            -->
            <div
              class="bt-event px-1 py-1"
              :style="{ color: event.textColor }"
            >
              <!--
                La barra de cupo va ARRIBA de todo. Abajo del título quedaba
                fuera del bloque en cuanto la salida era corta: se cortaba justo
                el dato que la barra existe para dar. Arriba tiene el ancho
                completo y no compite con nada.
              -->
              <div
                v-if="occupancyPercent(event.extendedProps.bt) !== null"
                class="bt-event__bar mb-1"
              >
                <div
                  class="bt-event__fill"
                  :class="{ 'bt-event__fill--over': isOverCapacity(event.extendedProps.bt) }"
                  :style="{
                    inlineSize: `${occupancyPercent(event.extendedProps.bt)}%`,
                    /*
                      El color PURO del servicio, no `currentcolor`: ese ahora es
                      el del texto, empujado al extremo contrario del lienzo para
                      poder leerse, y en tema claro dejaba la barra casi negra —
                      o sea sin decir de qué servicio es.

                      Pasada de cupo va sin color en línea, para que la regla de
                      `--over` pueda pintarla de rojo: un estilo en línea le
                      ganaría a la hoja de estilos.
                    */
                    backgroundColor: isOverCapacity(event.extendedProps.bt)
                      ? undefined
                      : event.borderColor,
                  }"
                />
              </div>

              <!--
                Primera línea: ÍCONO + hora + ocupación. Los tres datos que se
                leen de un vistazo, y los únicos que entran enteros en un bloque
                angosto. El título va debajo y se recorta a lo que quede libre:
                es lo más prescindible cuando el ícono ya dice de qué se trata.
              -->
              <div class="bt-event__head d-flex align-center ga-1">
                <VIcon
                  :icon="eventIcon(event.extendedProps.bt.taxonomyCode)"
                  size="13"
                  class="flex-shrink-0"
                />
                <span class="text-caption font-weight-medium flex-shrink-0">
                  {{ event.extendedProps.bt.time }}
                </span>
                <VSpacer />
                <span class="text-caption font-weight-bold flex-shrink-0 d-flex align-center ga-1">
                  <VIcon
                    icon="ri-group-line"
                    size="12"
                  />
                  {{ occupancyLabel(event.extendedProps.bt) || '—' }}
                </span>
              </div>

              <div class="bt-event__title text-caption">
                {{ event.title }}
              </div>

              <div
                v-if="event.extendedProps.bt.status === 'cancelled'"
                class="bt-event__head text-caption"
              >
                Cancelled
              </div>
            </div>
          </template>
        </FullCalendar>
      </VCardText>
    </VCard>

    <EventCreateDrawer
      v-model="showEditor"
      :dive-center-id="diveCenterId"
      :offerings="offerings"
      :staff="staff"
      :default-date="editorDate"
      :default-time="editorTime"
      :submitting="submitting"
      :error="editorError"
      @submit="submitCreate"
    />

    <BulkManageDrawer
      v-model="showBulk"
      :dive-center-id="diveCenterId"
      :events="selectedEvents"
      :staff="staff"
      @applied="onBulkApplied"
    />

    <EventDetailDrawer
      v-model="showDetail"
      :dive-center-id="diveCenterId"
      :offerings="offerings"
      :event="selected"
      :staff="staff"
      :submitting="submitting"
      :error="detailError"
      @save="onSave"
      @cancel-departure="onCancelDeparture"
      @reactivate="onReactivate"
      @delete="onDelete"
      @roster-changed="onRosterChanged"
    />

    <ClosuresDrawer
      v-model="showClosures"
      :dive-center-id="diveCenterId"
      :closures="closures"
      :editing="editingClosure"
      @saved="onClosuresSaved"
    />

    <ConfirmDialog ref="confirmRef" />

  </div>
</template>

<style lang="scss">
// El SCSS de FullCalendar vino con Materio y estaba sin usar: la demo de la
// plantilla lo importaba desde su propia página. Se importa acá, sin `scoped`,
// porque estiliza las clases globales que genera la librería.
@use "@core/scss/template/libs/full-calendar";

/*
 * Actividad tildada en modo selección.
 *
 * Se marca con un anillo y no cambiándole el color: el color del bloque
 * codifica el servicio (§20.8) y pisarlo perdería el dato que hace escaneable
 * la grilla de un vistazo. El anillo usa `box-shadow` en vez de `outline`
 * porque sigue el borde redondeado del bloque.
 */
.fc .bt-event--picked {
  box-shadow: 0 0 0 2px rgb(var(--v-theme-primary));
}

/*
 * Lo que ya terminó, apagado.
 *
 * La grilla mezcla en la misma semana lo que hay que preparar y lo que ya
 * ocurrió, y con el mismo peso visual los dos compiten: el lunes a la tarde,
 * media pantalla es historia. Se baja la opacidad del bloque entero —color,
 * texto y barra de cupo a la vez— en lugar de tocar cada color: el bloque sigue
 * siendo del color de su servicio, solo que en segundo plano.
 *
 * Se apaga, no se esconde: el pasado es el registro de la salida y desde ahí se
 * abre su roster y su planilla. Y vuelve a opacidad plena al pasar el mouse, que
 * es cuando alguien está yendo a buscarlo.
 */
.fc .bt-event--past {
  opacity: 0.45;
  transition: opacity 0.15s;
}

/*
 * En oscuro se apaga MENOS.
 *
 * El 45 % nació contra fondo blanco, donde bajar la opacidad acerca el bloque al
 * blanco y el resultado sigue siendo un bloque pálido pero visible. Sobre el
 * lienzo casi negro del tema oscuro el mismo 45 % lo acerca al negro, o sea que
 * lo borra: el pasado no quedaba en segundo plano, quedaba en ninguno. Con el
 * fondo del bloque ya más teñido (`EVENT_TINT.dark`), alcanza con un velo mucho
 * más liviano para que se lea como historia sin desaparecer.
 */
.v-theme--dark .fc .bt-event--past {
  opacity: 0.65;
}

.fc .bt-event--past:hover,
.v-theme--dark .fc .bt-event--past:hover {
  opacity: 1;
}

/*
 * El fondo de lo que ya pasó: días enteros y, en la columna de hoy, las horas
 * que ya transcurrieron.
 *
 * Va sobre el LIENZO y no sobre los bloques —eso ya se resuelve con la opacidad
 * de arriba—: sombrear el fondo separa la parte de la semana sobre la que
 * todavía se puede hacer algo de la que es historia, incluso donde no hay
 * ninguna salida dibujada. Sin esto, un lunes vacío y un viernes vacío se ven
 * igual.
 *
 * El tono sale del tema (`on-surface` al 4 %), así que funciona en claro y en
 * oscuro sin dos reglas. Es deliberadamente tenue: tiene que leerse como
 * "segundo plano", no como un estado.
 */
/*
 * Día cerrado por el centro (§22).
 *
 * Trama diagonal y no un color plano: el fondo plano ya lo usa la sombra del
 * pasado, y los dos se superponen cuando se cierra un día que además ya pasó.
 * La trama se sigue leyendo por debajo. Va en `::after` porque el `::before`
 * de la celda es la sombra de las horas transcurridas.
 */
/*
 * Día cerrado: trama diagonal sobre el fondo de la celda.
 *
 * 🪤 **FullCalendar copia las clases del día al popover de "+N more"**: su
 * class list termina siendo `fc-popover fc-more-popover fc-day … bt-day--closed`.
 * La versión anterior pintaba la trama con un `::after` absoluto, y para eso le
 * daba `position: relative` a `.bt-day--closed` — que al caer también sobre el
 * popover le pisaba su `position: absolute`. El popover colapsaba a 2 px de
 * ancho y fluía al final del documento: en un día cerrado se veía crecer la
 * barra de scroll y ningún panel, mientras que en un día normal abría bien.
 *
 * Como `background-image` no hace falta posicionar nada, así que el popover
 * conserva su posicionamiento. De yapa la trama queda DETRÁS de los eventos en
 * vez de encima, que es lo correcto: los bloques se leen sin nada superpuesto.
 */
.fc .bt-day--closed {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(var(--v-theme-on-surface), 0.07) 0,
    rgba(var(--v-theme-on-surface), 0.07) 6px,
    transparent 6px,
    transparent 12px
  );
}

// Misma herencia de clases: el popover NO va rayado. Adentro se listan las
// actividades del día y la trama de fondo las vuelve ilegibles.
.fc .fc-popover.bt-day--closed {
  background-image: none;
}

.fc .fc-day-past {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

/*
 * 🪤 **El popover de "+N more" hereda las clases del DÍA**, la misma trampa que
 * ya obligó a apagarle la trama de los días cerrados. Su class list termina
 * siendo `fc-popover fc-more-popover fc-day fc-day-past bt-day--closed`, así que
 * la sombra del pasado de acá arriba le pisa el fondo: en un día pasado el panel
 * salía con `rgba(on-surface, .04)` —o sea, prácticamente transparente— y se veía
 * la grilla y los eventos de atrás a través del contenido. En un día futuro no
 * pasaba, que es justo lo que se reportó.
 *
 * El fondo del panel se fija sólido y se gana por especificidad (0,3,0 contra
 * 0,2,0), sin `!important`.
 */
.fc .fc-popover.fc-more-popover {
  background-color: rgb(var(--v-theme-surface));
}

/*
 * Y adentro del panel, lo pasado va a opacidad PLENA.
 *
 * En la grilla se apaga para que no compita con lo que todavía hay que preparar,
 * y vuelve al pasar el mouse "que es cuando alguien está yendo a buscarlo".
 * Abrir el "+N more" de un día pasado es exactamente eso: nadie despliega un
 * panel para leer algo al 45 %.
 */
.fc .fc-popover .bt-event--past {
  opacity: 1;
}

/*
 * Hoy es media columna: hasta la hora actual, sombreada; de ahí en adelante,
 * limpia. FullCalendar marca los días enteros (`fc-day-past`) pero no tiene
 * nada por hora, así que la sombra es una capa propia cuya altura sale de
 * `--bt-day-elapsed` (la fracción del día visible ya transcurrida, calculada en
 * el script con la zona del centro).
 *
 * `z-index: 0` y `pointer-events: none`: es fondo, no puede taparle el click a
 * una salida ni a la celda vacía que abre el alta.
 */
.fc .fc-timegrid-col.fc-day-today .fc-timegrid-col-frame {
  position: relative;
}

.fc .fc-timegrid-col.fc-day-today .fc-timegrid-col-frame::before {
  position: absolute;
  z-index: 0;
  background-color: rgba(var(--v-theme-on-surface), 0.04);
  block-size: calc(var(--bt-day-elapsed, 0) * 100%);
  content: "";
  inset-block-start: 0;
  inset-inline: 0;
  pointer-events: none;
}

/*
 * La línea de "ahora".
 *
 * Se queda la **flecha** que trae FullCalendar y no un círculo: el marcador se
 * dibuja en el eje de horas, o sea fuera de la grilla, y ahí un punto suelto se
 * lee como algo que no pertenece al calendario. La flecha apunta hacia adentro
 * y ata el marcador a su línea.
 *
 * El color va por la variable de la librería, que pinta la línea **y** la
 * flecha de una vez: tocarlas por separado obliga a acertarle a la forma de la
 * flecha, que está hecha con bordes. El rojo sale del tema (`error`) en vez del
 * `red` por defecto, para que sea el mismo rojo que el resto del portal.
 */
.fc {
  --fc-now-indicator-color: rgb(var(--v-theme-error));
}

.fc .fc-timegrid-now-indicator-line {
  border-width: 2px 0 0;
}


/*
 * Los dos botones propios de la barra van con ícono, como las flechas.
 *
 * FullCalendar escribe el rótulo como `textContent` —no admite markup— y su
 * juego de íconos propio solo trae flechas. Así que el texto se esconde y el
 * ícono entra como máscara: es el mismo mecanismo que usa Iconify en este
 * proyecto (`mask` + `background-color: currentColor`), solo que con el SVG en
 * línea porque no hay dónde colgar la clase `ri-*`.
 *
 * El `title` del botón queda con el texto original, así que el tooltip nativo
 * y los lectores de pantalla siguen diciendo qué hace.
 */
/*
 * Un solo look para TODOS los botones de la barra: contorno verde sobre fondo
 * blanco, mismo alto y misma tipografía.
 *
 * La plantilla solo estiliza así al ÚLTIMO chunk (el conmutador Day/Week/Month)
 * porque es el único que le interesaba; `Today`, filtros y pantalla completa son
 * botones sueltos y caían al relleno oscuro por defecto de FullCalendar, así que
 * la barra mezclaba tres estilos distintos.
 *
 * 🪤 La especificidad tiene que superar a
 * `.fc-toolbar-chunk:last-child .fc-button-group .fc-button` de la plantilla
 * (0,4,0). De ahí el `.fc-button.fc-button-primary` doblado al final: 0,5,0.
 */
.fc .fc-header-toolbar .fc-toolbar-chunk .fc-button.fc-button-primary {
  border: 1px solid rgb(var(--v-theme-primary));
  background-color: transparent;
  color: rgb(var(--v-theme-primary));
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: 0.0187rem;

  // Mismo alto que los botones de la fila de acciones (`.bt-calendar-actions`).
  min-block-size: 38px;
  padding-inline: 0.75rem;
  text-transform: capitalize;

  &:hover:not(:disabled) {
    background-color: rgba(var(--v-theme-primary), var(--v-hover-opacity));
  }

  // El estado activo del conmutador de vistas, que la plantilla ya definía.
  &.fc-button-active {
    background-color: rgba(var(--v-theme-primary), var(--v-activated-opacity));
  }

  &:disabled {
    opacity: var(--v-disabled-opacity);
  }
}

/*
 * Dentro de un grupo los botones se tocan, así que el borde entre ellos se
 * comparte en vez de dibujarse dos veces.
 *
 * Se hace con un margen negativo y NO apagando el color del borde derecho: eso
 * dejaba la flecha izquierda con un lado descolorido, como si le faltara el
 * recuadro.
 */
.fc .fc-header-toolbar .fc-button-group .fc-button.fc-button-primary:not(:first-child) {
  margin-inline-start: -1px;
}

// Separación pareja entre los controles sueltos y los grupos.
.fc .fc-header-toolbar .fc-toolbar-chunk {
  gap: 0.5rem;
}

/*
 * 🪤 El selector arranca en `.fc` por ESPECIFICIDAD: FullCalendar declara
 * `.fc .fc-button { display: inline-block }` (0,2,0) y le gana a un
 * `.fc-btPanel-button` suelto (0,1,0). Sin el ancestro, el `display: inline-flex`
 * no se aplica y el ícono del `::before` —que es `display: block`— se apila
 * ENCIMA del rótulo en vez de ponerse a su lado.
 */
.fc .fc-btPanel-button,
.fc .fc-btFullscreen-button {
  // Ícono y rótulo en la misma línea. El `gap` no molesta en el de pantalla
  // completa, que no tiene texto: no hay segundo elemento que separar.
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;

  &::before {
    display: block;
    background-color: currentcolor;
    block-size: 1.125rem;
    content: "";
    inline-size: 1.125rem;
    mask-position: center;
    mask-repeat: no-repeat;
    mask-size: contain;
  }
}

/*
 * El botón de filtros va RELLENO mientras el panel está abierto.
 *
 * Es un interruptor y no una acción: sin estado visible, la única forma de
 * saber si el panel está desplegado es mirar si hay panel — que es justo lo que
 * uno no puede hacer cuando lo cerró sin querer. La clase la pone la tarjeta,
 * porque FullCalendar dibuja el botón y no acepta un binding.
 *
 * 🪤 El selector repite toda la cadena de la regla general de la barra
 * (0,5,0) más `.bt-panel-open`: con menos, el `background-color:
 * transparent` de aquella gana y el relleno no aparece nunca.
 */
/*
 * El halo gris de foco de FullCalendar, apagado en TODOS sus botones.
 *
 * 🪤 La plantilla ya lo apaga, pero solo dentro de un `.fc-button-group`
 * (`full-calendar.scss`), y los botones sueltos de la barra —Filters, pantalla
 * completa, Today— no están en ninguno. Ahí quedaba el `box-shadow` gris azulado
 * que trae la librería, y como el foco sobrevive al click, abrir o cerrar el
 * panel dejaba el botón con un reborde pegado hasta clickear en otro lado.
 *
 * Se apaga en `:focus` pero **se devuelve en `:focus-visible`**: quien navega con
 * el teclado necesita ver dónde está parado, y borrar el foco por completo para
 * limpiar el efecto del mouse dejaría la barra inutilizable sin él. `:focus-visible`
 * es justamente la distinción que el navegador ya sabe hacer.
 */
.fc .fc-header-toolbar .fc-button:focus {
  box-shadow: none;
}

.fc .fc-header-toolbar .fc-button:focus-visible {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.5);
}

.bt-panel-open .fc .fc-header-toolbar .fc-toolbar-chunk .fc-btPanel-button.fc-button-primary {
  background-color: rgba(var(--v-theme-primary), var(--v-activated-opacity));
}

.fc-btPanel-button::before {
  mask-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22currentColor%22%20d%3D%22M21%204v2h-1l-5%207.5V22H9v-8.5L4%206H3V4zM6.404%206L11%2012.894V20h2v-7.106L17.596%206z%22%2F%3E%3C%2Fsvg%3E");
}

.fc-btFullscreen-button::before {
  mask-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22currentColor%22%20d%3D%22M8%203v2H4v4H2V3zM2%2021v-6h2v4h4v2zm20%200h-6v-2h4v-4h2zm0-12h-2V5h-4V3h6z%22%2F%3E%3C%2Fsvg%3E");
}

// En pantalla completa el mismo botón sale, así que cambia el ícono.
.bt-calendar-card--fullscreen .fc-btFullscreen-button::before {
  mask-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22currentColor%22%20d%3D%22M18%207h4v2h-6V3h2zM8%209H2V7h4V3h2zm10%208v4h-2v-6h6v2zM8%2015v6H6v-4H2v-2z%22%2F%3E%3C%2Fsvg%3E");
}

// El menú lateral se va de la pantalla mientras dura la pantalla completa.
// Va sin `scoped` a propósito: el elemento es del layout, no de esta página.
body.bt-calendar-fullscreen {
  .layout-vertical-nav,
  .layout-overlay {
    display: none;
  }
}

/*
 * Pantalla completa. `position: fixed` y no la Fullscreen API — ver la nota en
 * el script. El z-index queda por debajo de los overlays de Vuetify para que el
 * panel de una salida se siga abriendo por encima.
 */
.bt-calendar-card--fullscreen {
  position: fixed;
  z-index: 1000;
  display: flex;
  overflow: auto;
  border-radius: 0;
  flex-direction: column;
  inset: 0;
  margin: 0;
}

/*
 * Los tres controles de la barra, a la misma altura.
 *
 * `VBtn` calcula su alto desde `size` y `VBtnGroup` desde `density`: son dos
 * escalas distintas que no coinciden, así que cualquier combinación de props
 * deja la fila desalineada por un par de píxeles. Se fija el alto una vez para
 * el conjunto en lugar de buscar el par de valores que casualmente empareje.
 */
/*
 * Dos columnas: leyenda a la izquierda, botones a la derecha, y los botones no
 * bajan nunca de línea.
 *
 * Antes era un solo `flex-wrap` con todo adentro, así que la leyenda crecía
 * hasta empujar los controles al renglón siguiente — y son los tres controles
 * principales de la pantalla.
 */
.bt-calendar-header {
  flex-wrap: nowrap;
}

.bt-calendar-legend {
  // `min-inline-size: 0` o el contenido impone su ancho mínimo y la columna se
  // niega a encogerse, que es lo que rompía el reparto.
  flex: 1 1 auto;
  min-inline-size: 0;
}

.bt-calendar-actions {
  flex: 0 0 auto;
  margin-inline-start: auto;

  .v-btn,
  .v-btn-group {
    block-size: 38px;
  }
}

// En pantallas angostas no hay ancho para las dos columnas: ahí sí se apilan,
// leyenda arriba y controles debajo.
@media (max-width: 959px) {
  .bt-calendar-header {
    flex-wrap: wrap;
  }

  .bt-calendar-actions {
    margin-inline-start: 0;
  }
}

/*
 * Ancho fijo del panel: con `flex-basis` en porcentaje el mini-calendario
 * reflowaba en cada cambio de vista del calendario grande.
 */
.bt-calendar-panel {
  // 300px y no menos: la grilla del `VDatePicker` son siete botones de 40px más
  // su padding, y por debajo de eso recorta los días en vez de encogerlos.
  inline-size: 300px;

  /*
   * Divisoria contra el calendario. En la plantilla el panel es un
   * `VNavigationDrawer` dentro de un `VLayout` y la línea es su propio borde;
   * acá es un div, así que se dibuja a mano con el mismo token de borde. Sin
   * ella los filtros y la grilla se leían como una sola superficie y las
   * casillas parecían pertenecer al calendario.
   */
  border-inline-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-inline-end: 1rem;
}

/*
 * El mini calendario va sin recuadro.
 *
 * `VDatePicker` es una `VCard` por dentro y trae su propio borde según el tema,
 * que aparecía o no según cuándo se montara — de ahí el borde intermitente. Se
 * apaga explícitamente en vez de confiar en el default.
 */
/*
 * Los cierres se abren al click: sin el cursor y sin respuesta al hover no se
 * ven pulsables, que es lo que los diferencia del resto del panel.
 *
 * El gris del recuadro NO se pinta acá: es `VCard variant="outlined"`, que usa
 * el token de borde del tema — el mismo gris claro que el contorno de un campo.
 *
 * 🪤 Se probó antes con `VAlert variant="outlined" color="secondary"`, que es
 * la receta de la plantilla para un recuadro gris, y salía casi negro: este
 * proyecto **redefinió `secondary`** al gris 700 del sitio (#565973) en vez del
 * gris claro de Materio (ver `plugins/vuetify/theme.ts`). Las recetas de la
 * plantilla que dependan de `secondary` no dan el tono original acá.
 */
.bt-closure-chip.v-card {
  cursor: pointer;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.04);
  }
}

.bt-mini-calendar {
  border: none;

  &.v-card {
    border: none;
    box-shadow: none;
  }
}

.bt-calendar {
  // El color del bloque lo pone FullCalendar en línea, desde el servicio (ver
  // `serviceColors.ts`): solo se declara la FORMA del borde, para que ese color
  // caiga en la barra lateral izquierda y no en un recuadro completo.
  .fc-event {
    border-style: solid;
    border-width: 0;
    border-inline-start-width: 3px;
    cursor: pointer;
  }

  .bt-event--cancelled .bt-event {
    text-decoration: line-through;
  }

  /*
   * El bloque es una columna que ocupa todo el alto del evento. Sin esto el
   * contenido se dibujaba a su alto natural y se derramaba fuera de una salida
   * corta, que es lo que hacía que el título invadiera el bloque de abajo.
   */
  .bt-event {
    display: flex;
    overflow: hidden;
    flex-direction: column;
    block-size: 100%;
    inline-size: 100%;
    line-height: 1.25;
  }

  /*
   * 🪤 **`text-caption` trae su propio color y pisa el del evento.**
   *
   * La plantilla la redefine en `@core/scss/template/libs/vuetify/_overrides.scss`
   * con `color: rgba(on-background, --v-disabled-opacity)`. Es un color EXPLÍCITO
   * sobre el elemento, así que le gana a lo que herede del bloque: la hora, la
   * ocupación y el título salían en gris de "deshabilitado" mientras el ícono
   * —que no lleva la clase— sí tomaba el color del servicio. De ahí el síntoma
   * exacto: "solo el ícono tiene el color del evento".
   *
   * Se devuelve la herencia acá adentro en vez de sacar `text-caption` del
   * marcado: la clase también trae tamaño, interlineado y espaciado, que sí se
   * quieren.
   */
  .bt-event .text-caption {
    color: inherit;
  }

  // La hora, la ocupación y la barra nunca se ceden: es lo que hay que leer.
  .bt-event__head,
  .bt-event__bar {
    flex-shrink: 0;
  }

  /*
   * El título se queda con lo que sobre. El `line-clamp` alto es solo un tope:
   * quien recorta de verdad es el alto disponible del bloque, así que una
   * salida de una hora muestra dos líneas y un curso de cinco muestra las que
   * entren.
   */
  .bt-event__title {
    display: -webkit-box;
    color: inherit;
    overflow: hidden;
    flex: 1 1 auto;
    min-block-size: 0;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 6;

    /*
     * En una columna angosta —cuatro salidas a la misma hora— la plantilla
     * partía las palabras por la mitad y "Open Water" quedaba como "Op / en /
     * Wa / ter". Cortar la palabra entera es más legible que deletrearla en
     * vertical: se lee "Open" y el ícono ya dice de qué se trata.
     */
    word-break: normal;
    overflow-wrap: normal;
  }

  .bt-event__bar {
    border-radius: 2px;
    background-color: rgba(var(--v-theme-on-surface), 0.16);
    block-size: 3px;
  }

  /*
   * La hora de la vista de día, legible.
   *
   * La plantilla pinta `.fc-list-event-time` y `.fc-list-event-title` a medium
   * emphasis (~68 % de opacidad), que en una agenda del día deja el dato más
   * consultado de la pantalla —a qué hora sale— en gris claro sobre blanco. Es la
   * columna que se recorre con el dedo a la mañana: va en alto contraste.
   *
   * El título no se toca acá: su contenido es nuestro y ya toma el color del
   * servicio por estilo en línea.
   *
   * 🪤 El selector lleva una clase de más a propósito. La regla de la plantilla
   * es `.fc .fc-list .fc-list-event-time` (0,3,0); con `.fc .fc-list-event-time`
   * se pierde por especificidad, y empatarla dejaría el resultado a merced del
   * orden de carga de las hojas. Con `.fc-list-event` en el medio gana siempre.
   */
  .fc-list .fc-list-event .fc-list-event-time {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    font-weight: 500;
  }

  /*
   * La barra de cupo no puede ir en `currentcolor` a secas: ahora ese color es
   * el del TEXTO —empujado al extremo contrario del lienzo para poder leerse— y
   * en tema claro eso la dejaba casi negra, o sea sin decir de qué servicio es.
   * Toma el color puro del servicio, que es el del borde del bloque.
   */

  .bt-event__fill {
    border-radius: 2px;
    block-size: 100%;
  }

  /*
   * Pasado de cupo: rojo, y no el color del servicio.
   *
   * Es la única condición del bloque que exige una decisión —sacar a alguien o
   * subir el cupo— y con la barra pintada del color del servicio se veía igual
   * que una salida completa, que no exige nada.
   */
  .bt-event__fill--over {
    background-color: rgb(var(--v-theme-error));
  }
}

</style>
